import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDragAndDrop } from '@formkit/drag-and-drop/react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '../../contexts/ThemeContext';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import { estadisticasApi } from '../../services/api';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { StatisticsConfig } from '../../pages/dashboard/StatisticsPage';
import { 
  Bars3Icon,
  ArrowsUpDownIcon
} from '@heroicons/react/24/outline';
import {
  EstadisticaCitasPorMes,
  EstadisticaCancelacionesPorMes,
  TratamientoSolicitado,
  RankingProfesionales,
  DistribucionGeneroEdad,
  IngresosPorTratamiento,
  HorasPico
} from '../../types/estadisticas';

interface ChartItem {
  id: string;
  title: string;
  type: 'line' | 'bar' | 'pie' | 'composed' | 'table' | 'heatmap';
  component: React.ReactNode;
  enabled: boolean;
  gridCols?: number; // Para controlar el ancho (1 = mitad, 2 = completo)
}

interface DraggableStatisticsProps {
  config: StatisticsConfig;
}

export function DraggableStatistics({ config }: DraggableStatisticsProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();

  // Estado para filtros globales
  const today = new Date();
  const getMonday = (d: Date) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1); // lunes
    return new Date(date.setDate(diff));
  };
  const getSunday = (d: Date) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + 7; // domingo
    return new Date(date.setDate(diff));
  };
  const pad = (n: number) => n.toString().padStart(2, '0');
  const defaultYear = today.getFullYear();
  const defaultMonth = `${today.getFullYear()}-${pad(today.getMonth() + 1)}`;
  const defaultDay = today.toISOString().slice(0, 10);
  const monday = getMonday(today);
  const sunday = getSunday(today);
  const defaultWeekStart = monday.toISOString().slice(0, 10);
  const defaultWeekEnd = sunday.toISOString().slice(0, 10);

  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [fechaInicio, setFechaInicio] = useState(defaultMonth + '-01');
  const [fechaFin, setFechaFin] = useState(() => {
    const y = today.getFullYear();
    const m = today.getMonth() + 1;
    const lastDay = new Date(y, m, 0).getDate();
    return `${y}-${pad(m)}-${lastDay}`;
  });

  // Ajustar fechas automáticamente según agrupación
  useEffect(() => {
    if (groupBy === 'day') {
      setFechaInicio(defaultDay);
      setFechaFin(defaultDay);
    } else if (groupBy === 'week') {
      setFechaInicio(defaultWeekStart);
      setFechaFin(defaultWeekEnd);
    } else if (groupBy === 'month') {
      setFechaInicio(defaultMonth + '-01');
      const y = today.getFullYear();
      const m = today.getMonth() + 1;
      const lastDay = new Date(y, m, 0).getDate();
      setFechaFin(`${y}-${pad(m)}-${lastDay}`);
    } else if (groupBy === 'year') {
      setFechaInicio(`${defaultYear}-01-01`);
      setFechaFin(`${defaultYear}-12-31`);
    }
    // eslint-disable-next-line
  }, [groupBy]);

  // Lógica para sincronizar y validar fechas según agrupación
  const [warning, setWarning] = useState<string | null>(null);
  useEffect(() => {
    setWarning(null);
    const start = new Date(fechaInicio);
    const end = new Date(fechaFin);
    const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    if (groupBy === 'day' && fechaInicio !== fechaFin) {
      setWarning('Solo puedes seleccionar un único día.');
    }
    if (groupBy === 'week' && diffDays !== 6) {
      setWarning('La semana debe ser de lunes a domingo.');
    }
    if (groupBy === 'month') {
      const startDate = new Date(fechaInicio);
      const endDate = new Date(fechaFin);
      if (startDate.getFullYear() !== endDate.getFullYear() || startDate.getMonth() !== endDate.getMonth()) {
        setWarning('El mes debe ser completo.');
      }
    }
    if (groupBy === 'year') {
      const startDate = new Date(fechaInicio);
      const endDate = new Date(fechaFin);
      if (startDate.getFullYear() !== endDate.getFullYear()) {
        setWarning('El año debe ser completo.');
      }
    }
  }, [fechaInicio, fechaFin, groupBy]);

  // Mostrar resumen visual del filtro activo
  function resumenFiltro() {
    const start = new Date(fechaInicio);
    const end = new Date(fechaFin);
    const opciones: Intl.DateTimeFormatOptions =
      groupBy === 'year' ? { year: 'numeric' } :
      groupBy === 'month' ? { year: 'numeric', month: 'long' } :
      { year: 'numeric', month: 'long', day: 'numeric' };
    return `Mostrando datos de ${start.toLocaleDateString('es-ES', opciones)} a ${end.toLocaleDateString('es-ES', opciones)} agrupados por ${groupByLabel[groupBy]}`;
  }

  // Filtros para pasar a la API
  const filtros = useMemo(() => ({
    fecha_inicio: fechaInicio,
    fecha_fin: fechaFin,
    group_by: groupBy
  }), [fechaInicio, fechaFin, groupBy]);

  // Estilos personalizados para tooltips según el tema
  const tooltipStyles = {
    contentStyle: {
      backgroundColor: theme === 'dark' ? '#374151' : '#ffffff',
      border: theme === 'dark' ? '1px solid #4B5563' : '1px solid #E5E7EB',
      borderRadius: '6px',
      color: theme === 'dark' ? '#F9FAFB' : '#111827',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: theme === 'dark' 
        ? '0 10px 15px -3px rgba(0, 0, 0, 0.3)' 
        : '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    },
    labelStyle: {
      color: theme === 'dark' ? '#F9FAFB' : '#111827',
      fontWeight: '600'
    }
  };

  // Consultas para los datos
  const { data: citasAgrupadas = [], isLoading: loadingCitas } = useQuery<any[]>({
    queryKey: ['estadisticas', 'citas-agrupadas', filtros, config],
    queryFn: () => estadisticasApi.getCitasAgrupadas(filtros),
  });
  const { data: cancelacionesAgrupadas = [], isLoading: loadingCancelaciones } = useQuery<any[]>({
    queryKey: ['estadisticas', 'cancelaciones-agrupadas', filtros, config],
    queryFn: () => estadisticasApi.getCancelacionesAgrupadas(filtros),
  });
  const { data: tasaCancelacionAgrupada = [], isLoading: loadingTasaCancelacion } = useQuery<any[]>({
    queryKey: ['estadisticas', 'tasa-cancelacion-agrupada', filtros, config],
    queryFn: () => estadisticasApi.getTasaCancelacionAgrupada(filtros),
  });
  const { data: tratamientosSolicitadosAgrupados = {}, isLoading: loadingTratamientos } = useQuery<any>({
    queryKey: ['estadisticas', 'tratamientos-mas-solicitados-agrupados', filtros, config],
    queryFn: () => estadisticasApi.getTratamientosMasSolicitadosAgrupados(filtros),
  });
  const { data: ingresosPorTratamientoAgrupados = [], isLoading: loadingIngresos } = useQuery<any[]>({
    queryKey: ['estadisticas', 'ingresos-por-tratamiento-agrupados', filtros, config],
    queryFn: () => estadisticasApi.getIngresosPorTratamientoAgrupados(filtros),
  });
  const { data: rankingProfesionalesAgrupado = {}, isLoading: loadingRanking } = useQuery<any>({
    queryKey: ['estadisticas', 'ranking-profesionales-agrupado', filtros, config],
    queryFn: () => estadisticasApi.getRankingProfesionalesAgrupado(filtros),
  });
  const { data: especialidadesDemandadasAgrupadas = {}, isLoading: loadingEspecialidades } = useQuery<any>({
    queryKey: ['estadisticas', 'especialidades-demandadas-agrupadas', filtros, config],
    queryFn: () => estadisticasApi.getEspecialidadesDemandadasAgrupadas(filtros),
  });
  const { data: horasPicoAgrupadas = [], isLoading: loadingHorasPico } = useQuery<any[]>({
    queryKey: ['estadisticas', 'horas-pico-agrupadas', filtros, config],
    queryFn: () => estadisticasApi.getHorasPicoAgrupadas(filtros),
  });
  const { data: distribucionGeneroEdad = [], isLoading: loadingDistribucionGeneroEdad } = useQuery<any[]>({
    queryKey: ['estadisticas', 'distribucion-genero-edad'],
    queryFn: () => estadisticasApi.getDistribucionGeneroEdad(),
  });

  // Colores para los gráficos
  const COLORS = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', 
    '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'
  ];

  // Detectar la clave de agrupación y el label según groupBy
  const groupByLabel = {
    day: 'Día',
    week: 'Semana',
    month: 'Mes',
    year: 'Año'
  };
  const groupByKey = groupBy;
  const periodoKey = (citasAgrupadas[0] && (citasAgrupadas[0].periodo ? 'periodo' : groupByKey)) || 'periodo';

  // Crear los elementos de gráficas basados en la configuración
  const chartItems = useMemo(() => {
    const items: ChartItem[] = [];

    // Citas por periodo
    if (config.citasPorMes) {
      items.push({
        id: 'citas-por-mes',
        title: `Citas por ${groupByLabel[groupBy] || 'Periodo'}`,
        type: 'line',
        enabled: true,
        gridCols: 1,
        component: (
          <ResponsiveContainer width="100%" height={300}>
            {citasAgrupadas.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">Sin datos</div>
            ) : (
              <LineChart data={citasAgrupadas}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={periodoKey} label={{ value: groupByLabel[groupBy], position: 'insideBottom', offset: -5 }} />
                <YAxis />
                <Tooltip 
                  contentStyle={tooltipStyles.contentStyle}
                  labelStyle={tooltipStyles.labelStyle}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="total_citas"
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  name="Citas"
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        )
      });
    }

    // Cancelaciones por periodo
    if (config.cancelacionesPorMes) {
      items.push({
        id: 'cancelaciones-por-mes',
        title: `Cancelaciones por ${groupByLabel[groupBy] || 'Periodo'}`,
        type: 'bar',
        enabled: true,
        gridCols: 1,
        component: (
          <ResponsiveContainer width="100%" height={300}>
            {cancelacionesAgrupadas.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">Sin datos</div>
            ) : (
              <BarChart data={cancelacionesAgrupadas}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={periodoKey} label={{ value: groupByLabel[groupBy], position: 'insideBottom', offset: -5 }} />
                <YAxis />
                <Tooltip 
                  contentStyle={tooltipStyles.contentStyle}
                  labelStyle={tooltipStyles.labelStyle}
                />
                <Legend />
                <Bar 
                  dataKey="total_cancelaciones"
                  fill="#ef4444"
                  name="Cancelaciones"
                />
              </BarChart>
            )}
          </ResponsiveContainer>
        )
      });
    }

    // Tratamientos más solicitados por periodo
    if (config.topTratamientos) {
      // Asegurarse de que sea un array y mostrar el periodo más reciente si es agrupado
      let tratamientosArray: any[] = [];
      if (Array.isArray(tratamientosSolicitadosAgrupados)) {
        tratamientosArray = tratamientosSolicitadosAgrupados;
      } else if (tratamientosSolicitadosAgrupados && typeof tratamientosSolicitadosAgrupados === 'object') {
        const periodos = Object.keys(tratamientosSolicitadosAgrupados);
        if (periodos.length > 0) {
          const periodoMasReciente = periodos.sort().reverse()[0];
          tratamientosArray = tratamientosSolicitadosAgrupados[periodoMasReciente] || [];
        }
      }
      items.push({
        id: 'top-tratamientos',
        title: 'Tratamientos Más Solicitados',
        type: 'bar',
        enabled: true,
        gridCols: 1,
        component: (
          <ResponsiveContainer width="100%" height={300}>
            {tratamientosArray.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">Sin datos</div>
            ) : (
              <BarChart 
                data={tratamientosArray.slice(0, 5).map((item: any) => ({
                  nombre: item.tratamiento || item.nombre,
                  total: Number(item.total_citas || item.total || item.sesiones)
                }))}
                margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="nombre" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  fontSize={10}
                />
                <YAxis />
                <Tooltip 
                  contentStyle={tooltipStyles.contentStyle}
                  labelStyle={tooltipStyles.labelStyle}
                  formatter={(value) => [value, 'Citas']}
                  labelFormatter={(label) => `Tratamiento: ${label}`}
                />
                <Bar 
                  dataKey="total" 
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            )}
          </ResponsiveContainer>
        )
      });
    }

    if (config.rankingProfesionales) {
      // Asegurarse de que sea un array y mostrar el periodo más reciente si es agrupado
      let rankingArray: any[] = [];
      if (Array.isArray(rankingProfesionalesAgrupado)) {
        rankingArray = rankingProfesionalesAgrupado;
      } else if (rankingProfesionalesAgrupado && typeof rankingProfesionalesAgrupado === 'object') {
        const periodos = Object.keys(rankingProfesionalesAgrupado);
        if (periodos.length > 0) {
          // Tomar el periodo más reciente
          const periodoMasReciente = periodos.sort().reverse()[0];
          rankingArray = rankingProfesionalesAgrupado[periodoMasReciente] || [];
        }
      }
      items.push({
        id: 'ranking-profesionales',
        title: 'Ranking de Profesionales',
        type: 'table',
        enabled: true,
        gridCols: 1,
        component: (
          <div className="overflow-x-auto" style={{ height: '300px', overflowY: 'auto' }}>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Profesional
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Total Citas
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {rankingArray.slice(0, 10).map((item: any, index: number) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {index + 1}. {item.profesional}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {item.total || item.citas_totales}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      });
    }

    if (config.distribucionGenero) {
      // Usar los datos correctos de género
      const generoArray = distribucionGeneroEdad.filter((item: any) => item.genero);
      items.push({
        id: 'distribucion-genero',
        title: 'Distribución por Género',
        type: 'pie',
        enabled: true,
        gridCols: 1,
        component: (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={generoArray.map((item: any) => ({
                  name: item.genero,
                  value: Number(item.total) || 0,
                }))}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
                label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                labelLine={false}
              >
                {generoArray.map((entry: any, index: number) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={tooltipStyles.contentStyle}
                labelStyle={tooltipStyles.labelStyle}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )
      });
    }

    if (config.distribucionEdad) {
      // Usar los datos correctos de edad (mediana por género)
      const edadArray = distribucionGeneroEdad.filter((item: any) => item.genero && item.edad_mediana !== undefined);
      items.push({
        id: 'distribucion-edad',
        title: 'Edad Mediana por Género',
        type: 'pie',
        enabled: true,
        gridCols: 1,
        component: (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={edadArray.map((item: any) => ({
                  name: item.genero,
                  value: Number(item.edad_mediana) || 0,
                }))}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
                label={({ name, value, percent }) => `${name}: ${value} años`}
                labelLine={false}
              >
                {edadArray.map((entry: any, index: number) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[(index + 2) % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={tooltipStyles.contentStyle}
                labelStyle={tooltipStyles.labelStyle}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )
      });
    }

    if (config.ingresosTratamiento) {
      items.push({
        id: 'ingresos-tratamiento',
        title: 'Ingresos por Tratamiento',
        type: 'bar',
        enabled: true,
        gridCols: 1,
        component: (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ingresosPorTratamientoAgrupados?.slice(0, 6).map((item: any) => {
              const i = item as any;
              return {
                nombre: i.nombre,
                ingresos: Number(i.ingreso_estimado || i.ingresos)
              };
            })}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="nombre" 
                angle={-45}
                textAnchor="end"
                height={80}
                fontSize={12}
              />
              <YAxis 
                label={{ value: 'Ingresos (€)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                contentStyle={tooltipStyles.contentStyle}
                labelStyle={tooltipStyles.labelStyle}
                formatter={(value) => [`€${value}`, 'Ingresos']}
                labelFormatter={(label) => `Tratamiento: ${label}`}
              />
              <Legend />
              <Bar 
                dataKey="ingresos" 
                fill="#8b5cf6"
                name="Ingresos (€)"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )
      });
    }

    if (config.citasVsCancelaciones) {
      // Usar la clave 'periodo' en vez de 'mes'
      const combinedData = citasAgrupadas?.map((cita: any) => {
        const cancelacion = cancelacionesAgrupadas?.find((c: any) => c.periodo === cita.periodo);
        return {
          periodo: cita.periodo,
          citas: cita.total_citas || cita.total,
          cancelaciones: cancelacion?.total_cancelaciones || cancelacion?.total || 0,
          tasa: cancelacion && cita.total_citas ? ((cancelacion.total_cancelaciones / cita.total_citas) * 100).toFixed(1) : 0
        };
      });
      items.push({
        id: 'citas-vs-cancelaciones',
        title: 'Citas vs Cancelaciones por Periodo',
        type: 'composed',
        enabled: true,
        gridCols: 1,
        component: (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={combinedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="periodo" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip 
                contentStyle={tooltipStyles.contentStyle}
                labelStyle={tooltipStyles.labelStyle}
                formatter={(value, name) => {
                  if (name === 'tasa') return [`${value}%`, 'Tasa de cancelación'];
                  return [value, name];
                }}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="citas" fill="#3b82f6" name="Citas totales" />
              <Bar yAxisId="left" dataKey="cancelaciones" fill="#ef4444" name="Cancelaciones" />
              <Line 
                yAxisId="right" 
                type="monotone" 
                dataKey="tasa" 
                stroke="#f59e0b" 
                strokeWidth={3}
                name="Tasa cancelación (%)"
              />
            </ComposedChart>
          </ResponsiveContainer>
        )
      });
    }

    if (config.horasPico) {
      items.push({
        id: 'horas-pico',
        title: 'Horas Pico de Citas',
        type: 'heatmap',
        enabled: true,
        gridCols: 2, // Ocupa toda la fila
        component: (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Gráfico de barras */}
            <ResponsiveContainer width="100%" height={250}>
              <BarChart 
                data={horasPicoAgrupadas?.slice(0, 8)}
                margin={{ top: 5, right: 30, left: 20, bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="hora" 
                  fontSize={10}
                  angle={-45}
                  textAnchor="end"
                  height={40}
                />
                <YAxis />
                <Tooltip 
                  contentStyle={tooltipStyles.contentStyle}
                  labelStyle={tooltipStyles.labelStyle}
                  formatter={(value) => [value, 'Citas']}
                  labelFormatter={(label) => `Hora: ${label}`}
                />
                <Bar dataKey="total" fill="#06b6d4" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>

            {/* Tabla de horas */}
            <div className="overflow-hidden">
              <div className="grid grid-cols-4 gap-2 text-sm">
                {horasPicoAgrupadas?.map((hora: any, index: number) => (
                  <div 
                    key={index}
                    className={`p-3 rounded-lg text-center font-medium ${
                      hora.total > 5 
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                        : hora.total > 3
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                    }`}
                  >
                    <div className="font-bold">{hora.hora}</div>
                    <div className="text-xs">{hora.total} citas</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      });
    }

    return items;
  }, [config, citasAgrupadas, cancelacionesAgrupadas, tratamientosSolicitadosAgrupados, rankingProfesionalesAgrupado, especialidadesDemandadasAgrupadas, ingresosPorTratamientoAgrupados, horasPicoAgrupadas, distribucionGeneroEdad, theme]);

  // Versión simple con drag and drop manual basado en pointer events
  const [charts, setCharts] = useState<ChartItem[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Estado para el elemento que se está arrastrando
  const [draggedItem, setDraggedItem] = useState<ChartItem | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number>(-1);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  
  // Guardar una referencia al elemento que se está arrastrando
  const draggedElementRef = useRef<HTMLDivElement | null>(null);
  
  // Inicializar los elementos de gráficas desde chartItems cuando cambian
  useEffect(() => {
    if (chartItems.length > 0) {
      // Recuperar el orden guardado del localStorage
      const savedOrder = localStorage.getItem('statistics-order');
      if (savedOrder) {
        try {
          const orderIds = JSON.parse(savedOrder);
          // Reordenar según el orden guardado
          const orderedCharts = orderIds
            .map((id: string) => chartItems.find(chart => chart.id === id))
            .filter((chart: ChartItem | undefined): chart is ChartItem => chart !== undefined);
          
          // Agregar cualquier gráfica nueva que no esté en el orden guardado
          const existingIds = orderedCharts.map((chart: ChartItem) => chart.id);
          const newCharts = chartItems.filter((chart: ChartItem) => !existingIds.includes(chart.id));
          
          setCharts([...orderedCharts, ...newCharts]);
        } catch (e) {
          console.error('Error parsing saved order:', e);
          setCharts(chartItems);
        }
      } else {
        // Si no hay orden guardado, usar el orden por defecto
        setCharts(chartItems);
        // Guardar el orden inicial
        const orderIds = chartItems.map(chart => chart.id);
        localStorage.setItem('statistics-order', JSON.stringify(orderIds));
      }
    }
  }, [chartItems]);
  
  // Función para iniciar el arrastre
  const handleDragStart = (e: React.PointerEvent<HTMLDivElement>, item: ChartItem, index: number) => {
    // Solo manejar clicks en el handle de arrastre
    if (!(e.target as HTMLElement).closest('.drag-handle')) {
      return;
    }
    
    // Prevenir el comportamiento predeterminado
    e.preventDefault();
    
    // Capturar el puntero para recibir eventos incluso si sale del elemento
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    
    // Guardar el elemento que se está arrastrando
    const element = (e.currentTarget as HTMLDivElement);
    draggedElementRef.current = element;
    
    // Obtener la posición inicial del elemento y su tamaño
    const rect = element.getBoundingClientRect();
    
    // Aplicar estilo fijo para el elemento arrastrado
    element.style.width = `${rect.width}px`;
    element.style.height = `${rect.height}px`;
    element.style.position = 'absolute';
    element.style.top = `${rect.top}px`;
    element.style.left = `${rect.left}px`;
    element.style.zIndex = '1000';
    
    // Calcular el offset desde donde se hizo clic
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    
    // Guardar el estado
    setDraggedItem(item);
    setDraggedIndex(index);
    setDragOffset({ x: offsetX, y: offsetY });
    setDragPosition({ x: e.clientX - offsetX, y: e.clientY - offsetY });
    setIsDragging(true);
    
    // Aplicar clase CSS para indicar que se está arrastrando
    element.classList.add('dragging');
    
    // Crear un placeholder para mantener el espacio
    const placeholder = document.createElement('div');
    placeholder.className = 'drag-placeholder';
    placeholder.style.width = `${rect.width}px`;
    placeholder.style.height = `${rect.height}px`;
    placeholder.style.margin = '0';
    placeholder.style.padding = '0';
    placeholder.dataset.placeholder = 'true';
    
    // Insertar el placeholder después del elemento arrastrado
    element.parentNode?.insertBefore(placeholder, element.nextSibling);
  };
  
  // Función para actualizar la posición durante el arrastre
  const handleDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging || !draggedElementRef.current) return;
    
    // Actualizar la posición del elemento arrastrado
    const x = e.clientX - dragOffset.x;
    const y = e.clientY - dragOffset.y;
    
    // Aplicar la nueva posición
    draggedElementRef.current.style.transform = `translate3d(0, 0, 0)`;
    draggedElementRef.current.style.top = `${y}px`;
    draggedElementRef.current.style.left = `${x}px`;
    
    // Encontrar el elemento objetivo para soltar
    const elementsBelow = document.elementsFromPoint(e.clientX, e.clientY);
    
    // Buscar el primer elemento de gráfica (que no sea el placeholder ni el elemento arrastrado)
    const targetElement = elementsBelow.find(el => {
      if (el === draggedElementRef.current) return false;
      if ((el as HTMLElement).dataset?.placeholder === 'true') return false;
      return el.closest('[data-chart-id]') !== null;
    });
    
    if (targetElement) {
      const chartContainer = targetElement.closest('[data-chart-id]');
      if (chartContainer && chartContainer !== draggedElementRef.current) {
        const targetIndex = parseInt(chartContainer.getAttribute('data-chart-index') || '-1', 10);
        
        if (targetIndex !== -1 && targetIndex !== draggedIndex) {
          // Mover el placeholder a la nueva posición
          const placeholder = document.querySelector('.drag-placeholder');
          if (placeholder) {
            if (targetIndex > draggedIndex) {
              chartContainer.parentNode?.insertBefore(placeholder, chartContainer.nextSibling);
            } else {
              chartContainer.parentNode?.insertBefore(placeholder, chartContainer);
            }
          }
          
          // Reordenar la lista
          const newCharts = [...charts];
          const [removed] = newCharts.splice(draggedIndex, 1);
          newCharts.splice(targetIndex, 0, removed);
          
          // Actualizar el estado
          setCharts(newCharts);
          setDraggedIndex(targetIndex);
        }
      }
    }
  };
  
  // Función para finalizar el arrastre
  const handleDragEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging || !draggedElementRef.current) return;
    
    // Liberar el puntero
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    
    // Eliminar el placeholder
    const placeholder = document.querySelector('.drag-placeholder');
    if (placeholder) {
      placeholder.parentNode?.removeChild(placeholder);
    }
    
    // Resetear los estilos del elemento arrastrado
    draggedElementRef.current.style.position = '';
    draggedElementRef.current.style.top = '';
    draggedElementRef.current.style.left = '';
    draggedElementRef.current.style.width = '';
    draggedElementRef.current.style.height = '';
    draggedElementRef.current.style.transform = '';
    draggedElementRef.current.style.zIndex = '';
    
    // Remover la clase CSS
    draggedElementRef.current.classList.remove('dragging');
    
    // Guardar el nuevo orden en localStorage
    const orderIds = charts.map((chart: ChartItem) => chart.id);
    localStorage.setItem('statistics-order', JSON.stringify(orderIds));
    
    // Resetear el estado
    setDraggedItem(null);
    setDraggedIndex(-1);
    setIsDragging(false);
    draggedElementRef.current = null;
  };
  
  // Manejar el evento de orden aleatorio
  const handleRandomOrder = () => {
    const shuffled = [...charts].sort(() => Math.random() - 0.5);
    setCharts(shuffled);
    
    // Guardar el nuevo orden en localStorage
    const orderIds = shuffled.map(chart => chart.id);
    localStorage.setItem('statistics-order', JSON.stringify(orderIds));
  };
  
  // Mostrar spinner si alguna query está cargando
  const isLoadingAny = loadingCitas || loadingCancelaciones || loadingTratamientos || loadingRanking || loadingEspecialidades || loadingIngresos || loadingHorasPico || loadingDistribucionGeneroEdad;

  if (isLoadingAny) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text={t('dashboard.loadingStats')} />
      </div>
    );
  }

  if (charts.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 dark:text-gray-500 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          No hay estadísticas seleccionadas
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          Selecciona al menos una estadística para visualizar los datos
        </p>
      </div>
    );
  }

  // La implementación más sencilla para el drag and drop
  return (
    <div className="space-y-6">
      {/* Filtros globales de fechas y agrupación */}
      <div className="flex flex-wrap gap-4 items-end bg-gray-50 dark:bg-gray-800 p-4 rounded-lg mb-2">
        {groupBy === 'day' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Día</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={e => {
                setFechaInicio(e.target.value);
                setFechaFin(e.target.value);
              }}
              className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100"
            />
          </div>
        )}
        {groupBy === 'week' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Semana (elige cualquier día)</label>
            <input
              type="date"
              value={fechaInicio}
              onChange={e => {
                const date = new Date(e.target.value);
                const monday = getMonday(date);
                const sunday = getSunday(date);
                setFechaInicio(monday.toISOString().slice(0, 10));
                setFechaFin(sunday.toISOString().slice(0, 10));
              }}
              className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100"
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">De {fechaInicio} a {fechaFin}</div>
          </div>
        )}
        {groupBy === 'month' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Mes</label>
            <input
              type="month"
              value={fechaInicio.slice(0, 7)}
              onChange={e => {
                const [y, m] = e.target.value.split('-');
                setFechaInicio(`${y}-${m}-01`);
                const lastDay = new Date(Number(y), Number(m), 0).getDate();
                setFechaFin(`${y}-${pad(Number(m))}-${lastDay}`);
              }}
              className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100"
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">De {fechaInicio} a {fechaFin}</div>
          </div>
        )}
        {groupBy === 'year' && (
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Año</label>
            <input
              type="number"
              min="2000"
              max="2100"
              value={fechaInicio.slice(0, 4)}
              onChange={e => {
                setFechaInicio(`${e.target.value}-01-01`);
                setFechaFin(`${e.target.value}-12-31`);
              }}
              className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100"
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">De {fechaInicio} a {fechaFin}</div>
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Agrupar por</label>
          <select value={groupBy} onChange={e => setGroupBy(e.target.value as any)} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100">
            <option value="day">Día</option>
            <option value="week">Semana</option>
            <option value="month">Mes</option>
            <option value="year">Año</option>
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Resumen</label>
          <div className="text-xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded px-2 py-1">{resumenFiltro()}</div>
        </div>
      </div>
      {warning && (
        <div className="bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200 rounded-lg px-4 py-2 mb-2 text-sm">
          {warning}
        </div>
      )}
      {/* Estilos CSS para mejorar el drag and drop */}
      <style>{`
        .dragging {
          opacity: 0.8;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3);
          cursor: grabbing !important;
        }
        
        .drag-placeholder {
          border: 2px dashed #3b82f6;
          border-radius: 0.5rem;
          background-color: rgba(59, 130, 246, 0.1);
          margin-bottom: 1.5rem;
        }
        
        .drag-handle:hover {
          background-color: rgba(59, 130, 246, 0.1) !important;
          border-color: rgb(59, 130, 246) !important;
          cursor: grab;
        }
        
        .drag-handle:active {
          background-color: rgba(59, 130, 246, 0.2) !important;
          transform: scale(0.95) !important;
          cursor: grabbing;
        }
      `}</style>

      {/* Instrucciones de drag and drop */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
            <ArrowsUpDownIcon className="w-5 h-5" />
            <span className="text-sm font-medium">
              Reorganizar Gráficas
            </span>
          </div>
          <button
            onClick={handleRandomOrder}
            className="px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Orden Aleatorio
          </button>
        </div>
        <p className="text-xs text-blue-600 dark:text-blue-300 ml-7">
          Haz clic y arrastra el botón <span className="font-semibold">"Mover"</span> de cualquier gráfica para reorganizar el dashboard según tus preferencias.
        </p>
        <div className="mt-2 ml-7">
          <div className="inline-flex items-center gap-2 px-2 py-1 rounded border border-dashed border-blue-300 bg-blue-100 dark:bg-blue-800/30 text-blue-700 dark:text-blue-300">
            <Bars3Icon className="w-4 h-4" />
            <span className="text-xs font-medium">Mover</span>
          </div>
          <span className="text-xs text-blue-600 dark:text-blue-300 ml-2">← Botón de ejemplo</span>
        </div>
      </div>

      {/* Contenedor draggable */}
      <div 
        ref={containerRef} 
        className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative"
        onPointerMove={handleDragMove}
      >
        {charts.map((chart: ChartItem, index) => (
          <div
            key={chart.id}
            data-chart-id={chart.id}
            data-chart-index={index}
            className={`
              relative bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-lg transition-all duration-200
              ${chart.gridCols === 2 ? 'lg:col-span-2' : ''}
            `}
            onPointerDown={(e) => handleDragStart(e, chart, index)}
            onPointerUp={handleDragEnd}
            onPointerCancel={handleDragEnd}
            style={{
              touchAction: 'none', // Evita el scrolling al arrastrar en dispositivos táctiles
            }}
          >
            {/* Header con drag handle */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                {chart.title}
              </h3>
              <div className="drag-handle flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all select-none bg-gray-50 dark:bg-gray-700/50">
                <Bars3Icon className="w-5 h-5" />
                <span className="text-sm font-medium">
                  Mover
                </span>
              </div>
            </div>
            
            {/* Contenido de la gráfica */}
            <div className="chart-content">
              {chart.component}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 